import { useState } from 'react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import Card from '../ui/Card';

/**
 * Session Controls Component
 * Export and end session controls
 */

const SessionControls = ({
  sessionId,
  onExportData,
  onEndSession,
  onPauseSession,
  participantCount = 0,
  isMultiDay = false,
  isPaused = false
}) => {
  const [showEndModal, setShowEndModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf');
  const [endReason, setEndReason] = useState('');
  const [sendFeedbackForm, setSendFeedbackForm] = useState(true);

  const handleExport = () => {
    if (onExportData) {
      onExportData(exportFormat);
    }
    setShowExportModal(false);
  };

  const handleEndSession = () => {
    if (onEndSession) {
      onEndSession({
        reason: endReason,
        sendFeedbackForm,
      });
    }
    setShowEndModal(false);
  };

  return (
    <>
      <div className="flex items-center gap-1.5 sm:gap-3">
        {/* Export Button */}
        <Button variant="secondary" onClick={() => setShowExportModal(true)} className="!px-2 sm:!px-4">
          <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="hidden sm:inline">Export Data</span>
        </Button>

        {/* Pause Session Button - Only for multi-day sessions */}
        {isMultiDay && onPauseSession && (
          <Button
            variant={isPaused ? 'primary' : 'secondary'}
            onClick={onPauseSession}
            className="!px-2 sm:!px-4"
          >
            {isPaused ? (
              <>
                <svg className="w-4 h-4 sm:mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
                <span className="hidden sm:inline">Resume Session</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 sm:mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                </svg>
                <span className="hidden sm:inline">Pause Session</span>
              </>
            )}
          </Button>
        )}

        {/* End Session Button */}
        <Button variant="danger" onClick={() => setShowEndModal(true)} className="!px-2 sm:!px-4">
          <svg className="w-4 h-4 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span className="hidden sm:inline">End Session</span>
        </Button>
      </div>

      {/* Export Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Session Data"
      >
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Export all session data including activities, responses, and participant information.
          </p>

          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Export Format
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setExportFormat('pdf')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  exportFormat === 'pdf'
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span className="text-xs font-medium">PDF</span>
              </button>
              <button
                onClick={() => setExportFormat('excel')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  exportFormat === 'excel'
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <span className="text-xs font-medium">Excel</span>
              </button>
              <button
                onClick={() => setExportFormat('json')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  exportFormat === 'json'
                    ? 'border-primary-600 bg-primary-50 text-primary-700'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <svg className="w-6 h-6 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
                <span className="text-xs font-medium">JSON</span>
              </button>
            </div>
          </div>

          {/* What's Included */}
          <Card padding="sm" className="bg-neutral-50">
            <p className="text-xs font-semibold text-neutral-700 mb-2">What's included:</p>
            <ul className="text-xs text-neutral-600 space-y-1">
              <li className="flex items-center gap-2">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Session information and metadata
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                All activities and responses
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Participant list ({participantCount} participants)
              </li>
              <li className="flex items-center gap-2">
                <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Analytics and statistics
              </li>
            </ul>
          </Card>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button variant="primary" fullWidth onClick={handleExport}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export as {exportFormat.toUpperCase()}
            </Button>
            <Button variant="ghost" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* End Session Modal */}
      <Modal
        isOpen={showEndModal}
        onClose={() => setShowEndModal(false)}
        title="End Session"
      >
        <div className="space-y-4">
          {/* Warning */}
          <Card padding="sm" className="bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <p className="text-sm font-semibold text-amber-900 mb-1">Are you sure?</p>
                <p className="text-xs text-amber-800">
                  This will end the session for all {participantCount} participants. This action cannot be undone.
                </p>
              </div>
            </div>
          </Card>

          {/* End Reason */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Reason for ending (optional)
            </label>
            <textarea
              value={endReason}
              onChange={(e) => setEndReason(e.target.value)}
              placeholder="e.g., Session completed, Technical issues, etc."
              rows={3}
              className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Options */}
          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={sendFeedbackForm}
                onChange={(e) => setSendFeedbackForm(e.target.checked)}
                className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
              />
              <div>
                <p className="text-sm font-medium text-neutral-900">Send feedback form to participants</p>
                <p className="text-xs text-neutral-500">Collect feedback about the session</p>
              </div>
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <Button variant="danger" fullWidth onClick={handleEndSession}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              End Session Now
            </Button>
            <Button variant="ghost" onClick={() => setShowEndModal(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SessionControls;
